import React, { useState, useRef, useEffect } from 'react';
import { User, Crown, Zap } from 'lucide-react';
import { useAuthStore } from '../../../stores/authStore';
import { PersonalInfoModal } from './PersonalInfoModal';
import { MembershipModal } from './MembershipModal';
import { CreditsLedgerModal } from './CreditsLedgerModal';
import { AnnouncementsModal } from './AnnouncementsModal';
import { OPEN_MEMBERSHIP_EVENT } from '../../../utils/credits';

interface UserMenuProps {
  /** 紧凑模式类名由父级通过 CSS 控制，此处仅用于决定下拉菜单是否显示积分/会员项 */
  compact?: boolean;
  /** 打开 API 设置弹窗（紧凑模式下顶栏按钮已隐藏，入口移入下拉菜单） */
  onOpenSettings?: () => void;
  /** 打开上传作品弹窗：仅项目画布的 EditorLayout 传入（首页 TopNavigation 不传即无入口） */
  onOpenUpload?: () => void;
  /** 顶栏胶囊底色变体（设计稿）：default=首页 217:1195 透明底；canvas=画布 217:1229 实底 */
  variant?: 'default' | 'canvas';
}

// 自绘上传作品图标（顶栏按钮与紧凑菜单行共用，currentColor 跟随文字色）
export const UploadGlyph: React.FC<{ size?: number; strokeWidth?: number; className?: string }> = ({ size = 16, strokeWidth = 1.8, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className={className}>
    <path d="M12 14.75V4.75" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
    <path d="M7.75 8.75 12 4.5l4.25 4.25" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4.75 15.25v2A2.75 2.75 0 0 0 7.5 20h9a2.75 2.75 0 0 0 2.75-2.75v-2" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
  </svg>
);

export const UserMenu: React.FC<UserMenuProps> = ({ compact = false, onOpenSettings, onOpenUpload, variant = 'default' }) => {
  const filled = variant === 'canvas';
  const { user, isLoggedIn, openLoginModal, logout } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  // 账户中心弹窗（Figma 168-223 / 169-32）：「个人中心」打开个人信息页，「水印设置」直接定位水印页
  const [infoOpen, setInfoOpen] = useState(false);
  const [watermarkOpen, setWatermarkOpen] = useState(false);
  // 订阅会员弹窗（Figma 158-25）：「会员中心」胶囊/紧凑菜单入口共用
  const [membershipOpen, setMembershipOpen] = useState(false);
  // 积分明细弹窗（P1）：「积分明细」菜单入口；402 全局事件仅跳会员页（见下方 useEffect）
  const [ledgerOpen, setLedgerOpen] = useState(false);
  // 通知弹窗（P2 运维平台「公告管理」）：「通知」菜单入口
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 复制 UID 到剪贴板（clipboard API 不可用时回退 execCommand），并短暂显示「已复制」
  const handleCopyUid = async () => {
    if (!user) return;
    const uid = String(user.id);
    try {
      await navigator.clipboard.writeText(uid);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = uid;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  useEffect(() => {
    return () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 402 积分不足：useScriptPrompts 通过 requestOpenMembership 派发全局事件，
  // UserMenu 挂载于三处全局顶栏（EditorLayout canvas / HomeTopBar / TopNavigation），任意一处响应即可直达会员页
  useEffect(() => {
    const openMembership = () => setMembershipOpen(true);
    window.addEventListener(OPEN_MEMBERSHIP_EVENT, openMembership);
    return () => window.removeEventListener(OPEN_MEMBERSHIP_EVENT, openMembership);
  }, []);

  if (!isLoggedIn || !user) {
    return (
      <button
        onClick={() => openLoginModal()}
        className="px-4 h-10 rounded-xl text-sm text-zinc-200 hover:opacity-80 transition-opacity flex items-center"
        style={{ backgroundColor: '#2D2D2D', border: '1px solid #4D4D4D' }}
      >
        登录 / 注册
      </button>
    );
  }

  // 总余额口径 = 订阅/购买积分 + 每日赠送积分（与积分明细页、后端 balance 汇总一致）
  const points = (user.points ?? 0) + (user.dailyPoints ?? 0);

  return (
    <div ref={containerRef} className="relative flex items-center gap-2" style={{ height: '40px' }}>
      {/* 发布作品入口：仅画布传入 onOpenUpload 时渲染，位于积分胶囊左侧。
          设计稿 217:1229（100x40 / #0D0D0D 底 / 1px #363636 / 圆角12）：
          发布_icon 16 左距 12、文字 14px Medium 白、图文间距 4（内容 16+4+56=76 居中即重合） */}
      {onOpenUpload && (
        <button
          onClick={onOpenUpload}
          title="发布作品"
          aria-label="发布作品"
          className="hidden h-10 w-[100px] items-center justify-center gap-1 rounded-xl border border-[#363636] bg-[#0D0D0D] text-sm font-medium text-white transition-colors hover:border-[#4D4D4D] hover:bg-[#2D2D2D] md:flex"
        >
          <img src="/icons/home/publish-icon.svg" alt="" width={16} height={16} />
          <span className="user-label">发布作品</span>
        </button>
      )}
      {/* 积分 + 会员中心 合并胶囊：设计稿 217:1197（150x40 / 圆角12）；canvas 变体实底 #0D0D0D（217:1229）；
          移动端隐藏（hidden md:flex），紧凑模式（nav-compact）下由 CSS 隐藏并移入下拉菜单 */}
      <div
        className={`rewards-capsule hidden h-10 items-center rounded-xl border border-[#363636] transition-colors hover:border-[#4D4D4D] hover:bg-[#2D2D2D] md:flex ${filled ? 'bg-[#0D0D0D]' : ''}`}
      >
        <div className="flex h-full items-center gap-1 pl-[15px]">
          <img src="/icons/home/token-icon.svg" alt="" width={12} height={12} />
          <span className="text-sm font-medium text-white">{points}</span>
        </div>
        <button className="flex h-full items-center gap-1 pl-3 pr-4 text-sm text-white" onClick={() => setMembershipOpen(true)}>
          <img src="/icons/home/member-icon.svg" alt="" width={16} height={16} />
          <span className="user-label">会员中心</span>
        </button>
      </div>

      {/* 用户信息胶囊：设计稿 217:1207（120x40 / 头像24 #0092FF / 昵称 #8BCDFF）；
          canvas 变体实底 #0D0D0D + 描边 #363636（217:1229）；两变体 hover 均与画布顶栏一致升亮一档 */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={`flex h-10 items-center gap-1 rounded-xl border border-[#363636] pl-[15px] pr-4 transition-colors hover:border-[#4D4D4D] hover:bg-[#2D2D2D] ${filled ? 'bg-[#0D0D0D]' : ''}`}
      >
        {user.avatar ? (
          <img src={user.avatar} alt={user.nickname} className="h-6 w-6 rounded-full object-cover" style={{ boxShadow: '0 0 0 1px rgba(45,45,45,0.13)' }} />
        ) : (
          <div className="flex h-6 w-6 items-center justify-center rounded-full text-white" style={{ backgroundColor: '#0092FF', boxShadow: '0 0 0 1px rgba(45,45,45,0.13)' }}>
            <User size={12} />
          </div>
        )}
        <span className="max-w-[80px] truncate text-sm user-label" style={{ color: '#8BCDFF' }}>{user.nickname}</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setIsOpen(false)} />
          {/* 设计稿 lanhu_neiye box_14：容器 420x268 / #181818 / 圆角 16px / 边框 #2D2D2D，图标均为设计稿切图；
              P1 新增「积分明细」项后实高 308px（268 + 40） */}
          <div
            className="absolute right-0 top-full mt-2 w-[420px] rounded-2xl shadow-2xl z-[150] animate-in fade-in zoom-in-95 duration-150"
            style={{ backgroundColor: '#181818', border: '1px solid #2D2D2D' }}
          >
            {/* 用户信息卡片 group_14：严格按设计稿定位 (#222 / 396x72 / 圆角 16px) */}
            <div className="relative mt-3 h-[72px] rounded-2xl" style={{ width: '396px', marginLeft: '12px', backgroundColor: '#222222' }}>
              {user.avatar ? (
                <img src={user.avatar} alt={user.nickname} className="absolute rounded-full object-cover" style={{ width: '36px', height: '36px', left: '12px', top: '18px' }} />
              ) : (
                <div className="absolute rounded-full" style={{ width: '36px', height: '36px', left: '12px', top: '18px', backgroundColor: '#0092FF' }} />
              )}
              <div className="absolute flex flex-col" style={{ left: '60px', top: '18px', height: '36px' }}>
                {/* 昵称行：设计稿 text-wrapper_22 宽 197px，超长截断 */}
                <div className="flex items-start" style={{ width: '197px' }}>
                  <span className="text-[14px] leading-[14px] text-white whitespace-nowrap truncate">{user.nickname}</span>
                  <span className="text-[12px] leading-[12px] whitespace-nowrap truncate ml-3" style={{ color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>
                    {user.phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2')}
                  </span>
                </div>
                {/* UID 行：不限宽，保证显示完整；复制按钮跟随文末 */}
                <span className="flex items-center whitespace-nowrap" style={{ color: 'rgba(255,255,255,0.65)', marginTop: '8px' }}>
                  <span className="text-[12px] leading-[12px]">UID：{user.id}</span>
                  <button
                    onClick={handleCopyUid}
                    title={copied ? '已复制' : '复制UID'}
                    className="flex items-center ml-1"
                  >
                    {copied && <span className="text-[12px] leading-[12px] text-white mr-1 whitespace-nowrap">已复制</span>}
                    <img src="/icons/user-menu/copy.svg" alt="复制UID" width={16} height={16} className={copied ? 'opacity-100' : 'opacity-65'} />
                  </button>
                </span>
              </div>
              {/* 右箭头 thumbnail_25：margin 28px 12px 0 87px → right 12px, top 28px */}
              <img src="/icons/user-menu/chevron-right.svg" alt="" width={16} height={16} className="absolute" style={{ right: '12px', top: '28px' }} />
            </div>

            {/* 紧凑模式：导航栏折叠时积分/会员中心/API设置移入菜单（设计稿无此项，样式对齐普通行） */}
            {compact && (
              <div>
                <div className="flex items-center gap-3 mt-3 pl-5 text-[14px] leading-[14px] text-white">
                  <Zap size={16} className="text-amber-400 shrink-0" />
                  积分
                  <span className="ml-auto mr-3">{points}</span>
                </div>
                <button
                  className="flex w-full items-center gap-3 mt-6 pl-5 text-[14px] leading-[14px] text-white hover:opacity-80 transition-opacity"
                  onClick={() => {
                    setIsOpen(false);
                    setMembershipOpen(true);
                  }}
                >
                  <Crown size={16} className="text-amber-400 shrink-0" />
                  会员中心
                </button>
                {onOpenUpload && (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onOpenUpload();
                    }}
                    className="flex w-full items-center gap-3 mt-6 pl-5 text-[14px] leading-[14px] text-white hover:opacity-80 transition-opacity"
                  >
                    <UploadGlyph size={16} className="shrink-0" />
                    上传作品
                  </button>
                )}
                {onOpenSettings && (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onOpenSettings();
                    }}
                    className="flex w-full items-center gap-3 mt-6 pl-5 text-[14px] leading-[14px] text-white hover:opacity-80 transition-opacity"
                  >
                    <img src="/icons/home/api-settings.svg" alt="API设置" width={16} height={16} className="shrink-0" />
                    API设置
                  </button>
                )}
              </div>
            )}

            {/* 菜单项通用结构：内容行 16px 高（icon x=20 / 文字 x=48，五行左对齐），
                hover 选中框为上下外扩 12px 的覆盖层（40px 高、396px 宽），不占布局空间，
                垂直节奏与设计稿 box_14 一致（设计稿总高 268px，P1 增补一项后 308px） */}
            {/* 个人中心 group_15：内容 108..124，hover 框 96..136；点击打开个人中心弹窗（168-223） */}
            <button
              onClick={() => {
                setIsOpen(false);
                setInfoOpen(true);
              }}
              className="group relative flex items-center rounded-lg transition-colors"
              style={{ width: '396px', marginLeft: '12px', height: '16px', marginTop: '24px', paddingLeft: '8px' }}
            >
              <div
                className="absolute inset-x-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ top: '-12px', bottom: '-12px', backgroundColor: '#2D2D2D' }}
              />
              <img src="/icons/user-menu/profile.svg" alt="个人中心" width={16} height={16} className="relative z-10 shrink-0" />
              <span className="relative z-10 text-[14px] leading-[14px] text-white ml-3" style={{ marginTop: '1px' }}>个人中心</span>
              {/* 右箭头随选中框出现 */}
              <img
                src="/icons/user-menu/chevron-right.svg"
                alt=""
                width={16}
                height={16}
                className="absolute z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ right: '12px', top: '0px' }}
              />
            </button>

            {/* 积分明细（P1 新增）：打开积分流水页（类型筛选 + 分页 + 变动后余额）；图标复用积分 token-icon */}
            <button
              onClick={() => {
                setIsOpen(false);
                setLedgerOpen(true);
              }}
              className="group relative flex items-center rounded-lg transition-colors"
              style={{ width: '396px', marginLeft: '12px', height: '16px', marginTop: '24px', paddingLeft: '8px' }}
            >
              <div
                className="absolute inset-x-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ top: '-12px', bottom: '-12px', backgroundColor: '#2D2D2D' }}
              />
              <img src="/icons/home/token-icon.svg" alt="积分明细" width={16} height={16} className="relative z-10 shrink-0" />
              <span className="relative z-10 text-[14px] leading-[14px] text-white ml-3" style={{ marginTop: '1px' }}>积分明细</span>
              {/* 右箭头随选中框出现 */}
              <img
                src="/icons/user-menu/chevron-right.svg"
                alt=""
                width={16}
                height={16}
                className="absolute z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ right: '12px', top: '0px' }}
              />
            </button>

            {/* 水印设置 box_30：内容 148..164，hover 框 136..176；点击打开账户中心弹窗并定位水印设置页（169-32） */}
            <button
              onClick={() => {
                setIsOpen(false);
                setWatermarkOpen(true);
              }}
              className="group relative flex items-center rounded-lg transition-colors"
              style={{ width: '396px', marginLeft: '12px', height: '16px', marginTop: '24px', paddingLeft: '8px' }}
            >
              <div
                className="absolute inset-x-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ top: '-12px', bottom: '-12px', backgroundColor: '#2D2D2D' }}
              />
              <img src="/icons/user-menu/watermark.svg" alt="水印设置" width={16} height={16} className="relative z-10 shrink-0" />
              <span className="relative z-10 text-[14px] leading-[14px] text-white ml-3" style={{ marginTop: '1px' }}>水印设置</span>
              {/* 右箭头随选中框出现 */}
              <img
                src="/icons/user-menu/chevron-right.svg"
                alt=""
                width={16}
                height={16}
                className="absolute z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ right: '12px', top: '0px' }}
              />
            </button>

            {/* 通知 box_31：内容 188..204，hover 框 176..216；点击打开公告弹窗（运维平台「公告管理」） */}
            <button
              onClick={() => {
                setIsOpen(false);
                setNotificationsOpen(true);
              }}
              className="group relative flex items-center rounded-lg transition-colors"
              style={{ width: '396px', marginLeft: '12px', height: '16px', marginTop: '24px', paddingLeft: '8px' }}
            >
              <div
                className="absolute inset-x-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ top: '-12px', bottom: '-12px', backgroundColor: '#2D2D2D' }}
              />
              <img src="/icons/user-menu/bell.svg" alt="通知" width={16} height={16} className="relative z-10 shrink-0" />
              <span className="relative z-10 text-[14px] leading-[14px] text-white ml-3" style={{ marginTop: '1px' }}>通知</span>
              {/* 右箭头随选中框出现 */}
              <img
                src="/icons/user-menu/chevron-right.svg"
                alt=""
                width={16}
                height={16}
                className="absolute z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ right: '12px', top: '0px' }}
              />
            </button>

            {/* 退出登录 box_32：内容 228..244，hover 框 216..256；距底 24px（P1 增补后容器总高 308px） */}
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="group relative flex items-center rounded-lg transition-colors"
              style={{ width: '396px', marginLeft: '12px', height: '16px', marginTop: '24px', marginBottom: '24px', paddingLeft: '8px' }}
            >
              <div
                className="absolute inset-x-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ top: '-12px', bottom: '-12px', backgroundColor: '#2D2D2D' }}
              />
              <img src="/icons/user-menu/logout.svg" alt="退出登录" width={16} height={16} className="relative z-10 shrink-0" />
              <span className="relative z-10 text-[14px] leading-[14px] text-white ml-3" style={{ marginTop: '1px' }}>退出登录</span>
              {/* 右箭头随选中框出现 */}
              <img
                src="/icons/user-menu/chevron-right.svg"
                alt=""
                width={16}
                height={16}
                className="absolute z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ right: '12px', top: '0px' }}
              />
            </button>
          </div>
        </>
      )}

      {/* 账户中心弹窗（Figma 168-223 / 169-32）：内部 createPortal 到 body，
          不受顶栏 wrapper 的 transform 缩放/包含块劫持影响；
          两个实例分别对应「个人中心 / 水印设置」入口，initialTab 决定初始页签 */}
      {infoOpen && <PersonalInfoModal onClose={() => setInfoOpen(false)} />}
      {watermarkOpen && <PersonalInfoModal initialTab="watermark" onClose={() => setWatermarkOpen(false)} />}
      {/* 订阅会员弹窗（Figma 158-25）：内部 createPortal 到 body，z-[250] 全屏页 */}
      {membershipOpen && <MembershipModal onClose={() => setMembershipOpen(false)} />}
      {/* 积分明细弹窗（P1）：内部 createPortal 到 body，z-[250] 全屏页 */}
      {ledgerOpen && <CreditsLedgerModal onClose={() => setLedgerOpen(false)} />}
      {/* 通知弹窗（P2 运维平台「公告管理」）：内部 createPortal 到 body，z-[260] 居中卡片 */}
      {notificationsOpen && <AnnouncementsModal onClose={() => setNotificationsOpen(false)} />}
    </div>
  );
};
